import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Layout } from "@/components/layout";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Loader2,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Save,
  Info,
} from "lucide-react";
import {
  getIncentiveMetrics,
  getIncentiveDrillDown,
  getManualIncentiveEntries,
  saveManualIncentiveEntry,
  type AgentIncentive,
  type IncentiveTicketDetail,
  type ManualIncentiveEntry,
} from "@/lib/api";

// Access control - page visible to these emails only
export const INCENTIVE_ALLOWED_EMAILS = [
  "ismael@atlas.kitchen",
  "shawn@atlas.kitchen",
  "sung@atlas.kitchen",
  "sunny@atlas.kitchen",
  "kairaa@atlas.kitchen",
];

// Agents eligible for incentive calculations
const ACTIVE_AGENT_EMAILS = [
  "shanneille@atlas.kitchen",
  "aaron@atlas.kitchen",
  "therese@atlas.kitchen",
];

function formatDuration(minutes: number): string {
  if (minutes === 0) return "-";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

interface MonthOption {
  index: string;
  label: string;
  startDate: string;
  endDate: string;
  periodKey: string;
}

function getMonthOptions(): MonthOption[] {
  const options: MonthOption[] = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1).toISOString();
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59).toISOString();
    const periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    options.push({ index: String(i), label, startDate, endDate, periodKey });
  }
  return options;
}

function getBiweeklyPeriods(periodKey: string): { periodId: string; label: string }[] {
  const [year, month] = periodKey.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const midDay = Math.ceil(daysInMonth / 2);
  return [
    {
      periodId: `${periodKey}-BW1`,
      label: `${month}/1 - ${month}/${midDay}`,
    },
    {
      periodId: `${periodKey}-BW2`,
      label: `${month}/${midDay + 1} - ${month}/${daysInMonth}`,
    },
  ];
}

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-amber-100 text-amber-700",
  "bg-pink-100 text-pink-700",
  "bg-cyan-100 text-cyan-700",
  "bg-emerald-100 text-emerald-700",
];

// ---------------------------------------------------------------------------
// Drill-down panel (renders inside agent card, not as a table row)
// ---------------------------------------------------------------------------
function DrillDownPanel({
  agentEmail,
  metric,
  metricLabel,
  startDate,
  endDate,
  onClose,
}: {
  agentEmail: string;
  metric: "volume" | "resolution" | "oneshot" | "rescue";
  metricLabel: string;
  startDate: string;
  endDate: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["incentive-drilldown", agentEmail, metric, startDate, endDate],
    queryFn: () => getIncentiveDrillDown(agentEmail, metric, startDate, endDate),
  });

  const tickets = data?.tickets ?? [];

  return (
    <div className="mt-3 border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-muted-foreground">
          {metricLabel} ({tickets.length} tickets)
        </h4>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
          <X className="h-3 w-3" />
        </Button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">No tickets found</p>
      ) : (
        <div className="max-h-60 overflow-y-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow className="text-[11px]">
                <TableHead className="py-1.5">Ticket</TableHead>
                <TableHead className="py-1.5">Merchant</TableHead>
                <TableHead className="py-1.5">Category</TableHead>
                <TableHead className="py-1.5">Assignee</TableHead>
                <TableHead className="py-1.5">Orig. Assignee</TableHead>
                <TableHead className="py-1.5 text-right">Resolution</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((t: IncentiveTicketDetail) => (
                <TableRow key={t.id} className="text-[11px]">
                  <TableCell className="py-1.5">
                    <a
                      href={`/tickets?ticket=${t.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {t.subject || t.id.substring(0, 10)}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </TableCell>
                  <TableCell className="py-1.5">{t.merchant_name || "-"}</TableCell>
                  <TableCell className="py-1.5">
                    <Badge variant="outline" className="text-[9px] py-0">{t.category}</Badge>
                  </TableCell>
                  <TableCell className="py-1.5 text-muted-foreground">
                    {t.assignee_email?.split("@")[0] || "-"}
                  </TableCell>
                  <TableCell className="py-1.5 text-muted-foreground">
                    {t.original_assignee_email?.split("@")[0] || "-"}
                  </TableCell>
                  <TableCell className="py-1.5 text-right font-mono">
                    {t.resolution_minutes != null ? formatDuration(t.resolution_minutes) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier tile helper — returns bg/border classes
// ---------------------------------------------------------------------------
function tierTileClass(
  state: "pass" | "miss" | "neutral" | "missing"
): string {
  switch (state) {
    case "pass":
      return "bg-emerald-50 border border-emerald-200";
    case "miss":
      return "bg-red-50/60 border border-red-100";
    case "neutral":
      return "bg-muted/40 border";
    case "missing":
      return "bg-muted/20 border border-dashed";
  }
}

// ---------------------------------------------------------------------------
// Agent card — collapsible
// ---------------------------------------------------------------------------
function AgentCard({
  agent,
  index,
  expanded,
  onToggle,
  startDate,
  endDate,
  inactive,
}: {
  agent: AgentIncentive;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  startDate: string;
  endDate: string;
  inactive?: boolean;
}) {
  const [drillDown, setDrillDown] = useState<
    "volume" | "resolution" | "oneshot" | "rescue" | null
  >(null);

  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const initial = agent.agentName.charAt(0).toUpperCase();

  const metricLabels: Record<string, string> = {
    volume: "Volume (Closed Tickets)",
    resolution: "Resolution Time (Non-Tech)",
    oneshot: "One-Shot Resolutions",
    rescue: "Rescue Tickets",
  };

  const toggleDrill = (m: "volume" | "resolution" | "oneshot" | "rescue") => {
    setDrillDown(drillDown === m ? null : m);
  };

  // Determine tile states
  const volumeState =
    agent.volumePass ? "pass" : "miss";
  const resolutionState =
    agent.resolutionTicketCount === 0
      ? "neutral"
      : agent.resolutionPass
        ? "pass"
        : "miss";
  const oneShotState = agent.oneShotCount > 0 ? "pass" : "neutral";
  const speedState =
    agent.speedAvgMinutes == null
      ? "missing"
      : agent.speedPass
        ? "pass"
        : "miss";
  const nomineeState = agent.nomineeCount > 0 ? "pass" : "neutral";

  return (
    <div className={`bg-card rounded-lg border shadow-sm overflow-hidden ${inactive ? "opacity-50" : ""}`}>
      {/* Header — always visible */}
      <button
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <div
            className={`w-7 h-7 rounded-full ${avatarColor} flex items-center justify-center text-xs font-bold shrink-0`}
          >
            {initial}
          </div>
          <div>
            <div className="font-medium text-sm">{agent.agentName}</div>
            <div className="text-xs text-muted-foreground">{agent.agentEmail}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Closed
            </div>
            <div className="font-semibold text-sm">{agent.volumeCount}</div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Avg Res.
            </div>
            <div className="font-semibold text-sm">
              {agent.resolutionAvgMinutes > 0
                ? formatDuration(agent.resolutionAvgMinutes)
                : "-"}
            </div>
          </div>
          <div className="min-w-[72px] text-right">
            {inactive ? (
              <span className="text-xs text-muted-foreground">Not eligible</span>
            ) : (
              <span
                className={`text-xl font-bold ${
                  agent.totalReward > 0 ? "text-emerald-600" : "text-muted-foreground/40"
                }`}
              >
                ${agent.totalReward}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Expanded — tier tiles */}
      {expanded && (
        <div className="px-4 pb-4 border-t bg-muted/10">
          <div className={`grid ${inactive ? "grid-cols-3" : "grid-cols-5"} gap-2 mt-3`}>
            {/* Volume */}
            <button
              onClick={() => toggleDrill("volume")}
              className={`rounded-lg p-2.5 text-center text-xs transition hover:ring-2 hover:ring-ring ${tierTileClass(inactive ? "neutral" : volumeState)}`}
            >
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                Volume
              </div>
              <div className="font-bold text-sm mt-1">
                {agent.volumeCount}{" "}
                {!inactive && <span className="font-normal text-muted-foreground">/ 50</span>}
              </div>
              {!inactive && (
                <div
                  className={`text-[10px] mt-1 ${
                    agent.volumePass
                      ? "text-emerald-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {agent.volumePass ? "+$30" : `need ${50 - agent.volumeCount} more`}
                </div>
              )}
            </button>

            {/* Resolution */}
            <button
              onClick={() => toggleDrill("resolution")}
              className={`rounded-lg p-2.5 text-center text-xs transition hover:ring-2 hover:ring-ring ${tierTileClass(inactive ? "neutral" : resolutionState)}`}
            >
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                Resolution
              </div>
              <div className="font-bold text-sm mt-1">
                {agent.resolutionAvgMinutes > 0
                  ? formatDuration(agent.resolutionAvgMinutes)
                  : "-"}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {agent.resolutionTicketCount} tickets
              </div>
              {!inactive && (
                <div
                  className={`text-[10px] mt-0.5 ${
                    agent.resolutionPass
                      ? "text-emerald-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {agent.resolutionTicketCount === 0
                    ? "n/a"
                    : agent.resolutionPass
                      ? "+$30"
                      : "target \u2264 4h"}
                </div>
              )}
            </button>

            {/* One-shot */}
            <button
              onClick={() => toggleDrill("oneshot")}
              className={`rounded-lg p-2.5 text-center text-xs transition hover:ring-2 hover:ring-ring ${tierTileClass(inactive ? "neutral" : oneShotState)}`}
            >
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                One-shot
              </div>
              <div className="font-bold text-sm mt-1">{agent.oneShotCount}</div>
              {!inactive && (
                <div
                  className={`text-[10px] mt-1 ${
                    agent.oneShotReward > 0
                      ? "text-emerald-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {agent.oneShotReward > 0 ? `+$${agent.oneShotReward}` : "$2/ea"}
                </div>
              )}
            </button>

            {/* Speed — only for active agents */}
            {!inactive && (
              <div
                className={`rounded-lg p-2.5 text-center text-xs ${tierTileClass(speedState)}`}
              >
                <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                  Speed
                </div>
                <div className="font-bold text-sm mt-1">
                  {agent.speedAvgMinutes != null ? `${agent.speedAvgMinutes}m` : "--"}
                </div>
                <div
                  className={`text-[10px] mt-1 ${
                    agent.speedAvgMinutes == null
                      ? "text-amber-500"
                      : agent.speedPass
                        ? "text-emerald-600"
                        : "text-muted-foreground"
                  }`}
                >
                  {agent.speedAvgMinutes == null
                    ? "enter in sidebar"
                    : agent.speedPass
                      ? "+$30"
                      : "target \u2264 4m"}
                </div>
              </div>
            )}

            {/* Nominee — only for active agents */}
            {!inactive && (
              <div
                className={`rounded-lg p-2.5 text-center text-xs ${tierTileClass(nomineeState)}`}
              >
                <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                  Nominee
                </div>
                <div className="font-bold text-sm mt-1">
                  {agent.nomineeCount}
                  <span className="font-normal text-muted-foreground"> pd</span>
                </div>
                <div
                  className={`text-[10px] mt-1 ${
                    agent.nomineeReward > 0
                      ? "text-emerald-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {agent.nomineeReward > 0
                    ? `+$${agent.nomineeReward}`
                    : "$30/pd"}
                </div>
              </div>
            )}
          </div>

          {/* Drill-down */}
          {drillDown && (
            <DrillDownPanel
              agentEmail={agent.agentEmail}
              metric={drillDown}
              metricLabel={metricLabels[drillDown]}
              startDate={startDate}
              endDate={endDate}
              onClose={() => setDrillDown(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Speed input — sidebar variant
// ---------------------------------------------------------------------------
function SpeedInput({
  agent,
  savedValue,
  periodKey,
  onSaved,
}: {
  agent: AgentIncentive;
  savedValue: string;
  periodKey: string;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(savedValue);
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    setValue(savedValue);
  }, [savedValue]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveManualIncentiveEntry({
        periodStart: periodKey,
        periodEnd: periodKey,
        agentEmail: agent.agentEmail,
        entryType: "speed",
        value: value || null,
        notes: null,
      }),
    onSuccess: () => {
      setStatus("saved");
      onSaved();
      setTimeout(() => setStatus("idle"), 2000);
    },
  });

  const isDirty = value !== savedValue;
  const hasValue = savedValue !== "" && !isDirty;
  const pass = hasValue && Number(savedValue) <= 4;

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs w-20 truncate text-muted-foreground">
        {agent.agentName}
      </span>
      <Input
        type="number"
        step="0.1"
        placeholder="--"
        className="w-16 h-7 text-xs text-right"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setStatus("idle");
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0"
        disabled={!isDirty || saveMutation.isPending}
        onClick={() => {
          setStatus("saving");
          saveMutation.mutate();
        }}
      >
        {saveMutation.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Save className="h-3 w-3" />
        )}
      </Button>
      <span className="w-10 text-right text-xs shrink-0">
        {status === "saved" ? (
          <Check className="h-3 w-3 text-emerald-600 inline" />
        ) : hasValue ? (
          pass ? (
            <span className="text-emerald-600">+$30</span>
          ) : (
            <span className="text-red-500">&gt;4m</span>
          )
        ) : null}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nominee row — biweekly, multi-select (up to 2), split $15
// ---------------------------------------------------------------------------
function NomineeRow({
  periodId,
  periodLabel,
  agents,
  savedValue,
  onSaved,
}: {
  periodId: string;
  periodLabel: string;
  agents: AgentIncentive[];
  savedValue: string;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const selectedEmails = savedValue ? savedValue.split(",").map((s) => s.trim()) : [];

  const saveMutation = useMutation({
    mutationFn: (newValue: string) =>
      saveManualIncentiveEntry({
        periodStart: periodId,
        periodEnd: periodId,
        agentEmail: null,
        entryType: "nominee",
        value: newValue || null,
        notes: null,
      }),
    onSuccess: () => {
      setStatus("saved");
      onSaved();
      setTimeout(() => setStatus("idle"), 2000);
    },
  });

  const toggleNominee = (email: string) => {
    let next: string[];
    if (selectedEmails.includes(email)) {
      next = selectedEmails.filter((e) => e !== email);
    } else {
      if (selectedEmails.length >= 2) return;
      next = [...selectedEmails, email];
    }
    setStatus("saving");
    saveMutation.mutate(next.join(","));
  };

  const perPerson = selectedEmails.length === 2 ? "$15" : "$30";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{periodLabel}</span>
        <span className="text-[10px] text-muted-foreground">
          {saveMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin inline" />
          ) : status === "saved" ? (
            <Check className="h-3 w-3 text-emerald-600 inline" />
          ) : selectedEmails.length > 0 ? (
            <span className="text-emerald-600">
              {selectedEmails.length === 2 ? `${perPerson} each` : perPerson}
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {agents.map((a) => {
          const selected = selectedEmails.includes(a.agentEmail);
          return (
            <button
              key={a.agentEmail}
              onClick={() => toggleNominee(a.agentEmail)}
              disabled={!selected && selectedEmails.length >= 2}
              className={`px-2 py-1 rounded text-[11px] border transition ${
                selected
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : selectedEmails.length >= 2
                    ? "bg-muted/20 border-transparent text-muted-foreground/40 cursor-not-allowed"
                    : "bg-muted/30 border-transparent text-muted-foreground hover:border-muted-foreground/30"
              }`}
            >
              {a.agentName}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
function Sidebar({
  periodKey,
  agents,
}: {
  periodKey: string;
  agents: AgentIncentive[];
}) {
  const queryClient = useQueryClient();
  const { data: manualData } = useQuery({
    queryKey: ["manual-entries", periodKey],
    queryFn: () => getManualIncentiveEntries(periodKey),
  });

  const entries = manualData?.entries ?? [];
  const biweekly = getBiweeklyPeriods(periodKey);
  const activeAgents = agents.filter((a) => ACTIVE_AGENT_EMAILS.includes(a.agentEmail));

  const getSpeedValue = (email: string) =>
    entries.find(
      (e: ManualIncentiveEntry) =>
        e.agent_email === email && e.entry_type === "speed"
    )?.value || "";

  const getNomineeForPeriod = (periodId: string) =>
    entries.find(
      (e: ManualIncentiveEntry) =>
        e.entry_type === "nominee" && e.period_start === periodId
    )?.value || "";

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["manual-entries", periodKey] });
    queryClient.invalidateQueries({ queryKey: ["incentive-metrics"] });
  };

  return (
    <div className="space-y-4">
      {/* Speed entries */}
      <div className="bg-card rounded-lg border p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-sm">Speed Times</h3>
          <span className="text-[10px] text-muted-foreground">
            from Periskope
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mb-3">
          {"Avg response time in minutes. \u2264 4 min = $30. Click save icon to persist."}
        </p>
        <div className="space-y-1.5">
          {activeAgents.map((agent) => (
            <SpeedInput
              key={agent.agentEmail}
              agent={agent}
              savedValue={getSpeedValue(agent.agentEmail)}
              periodKey={periodKey}
              onSaved={invalidateAll}
            />
          ))}
        </div>
      </div>

      {/* Biweekly nominees */}
      <div className="bg-card rounded-lg border p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-sm">Biweekly Nominees</h3>
          <span className="text-[10px] text-muted-foreground">$30/period</span>
        </div>
        <p className="text-[10px] text-muted-foreground mb-3">
          Quality + Collab award. Pick up to 2 nominees per period. If 2 winners, $30 is split.
        </p>
        <div className="space-y-3">
          {biweekly.map((bw) => (
            <NomineeRow
              key={bw.periodId}
              periodId={bw.periodId}
              periodLabel={bw.label}
              agents={activeAgents}
              savedValue={getNomineeForPeriod(bw.periodId)}
              onSaved={invalidateAll}
            />
          ))}
        </div>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function IncentivesPage() {
  const monthOptions = getMonthOptions();
  const [selectedIndex, setSelectedIndex] = useState("0");
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(
    new Set(ACTIVE_AGENT_EMAILS)
  );

  const selected =
    monthOptions.find((o) => o.index === selectedIndex) ?? monthOptions[0];
  const { startDate, endDate, periodKey } = selected;

  const { data, isLoading, error } = useQuery({
    queryKey: ["incentive-metrics", startDate, endDate],
    queryFn: () => getIncentiveMetrics(startDate, endDate),
    refetchInterval: 300000,
  });

  const toggleAgent = (email: string) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  return (
    <Layout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">
              Incentives &mdash; {selected.label}
            </h1>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-4">
                <h4 className="font-semibold text-sm mb-3">Tier Reference</h4>
                <div className="space-y-3 text-xs">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-medium">Speed</div>
                      <div className="text-muted-foreground">Avg response time &le; 4 min</div>
                      <div className="text-muted-foreground/70 text-[10px]">Manual entry from Periskope</div>
                    </div>
                    <span className="font-semibold whitespace-nowrap">$30 / month</span>
                  </div>
                  <div className="border-t" />
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-medium">Resolution</div>
                      <div className="text-muted-foreground">Avg &le; 4 hrs (non-tech tickets only)</div>
                      <div className="text-muted-foreground/70 text-[10px]">Credited to original assignee</div>
                    </div>
                    <span className="font-semibold whitespace-nowrap">$30 / month</span>
                  </div>
                  <div className="border-t" />
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-medium">Volume</div>
                      <div className="text-muted-foreground">&ge; 50 tickets closed in the month</div>
                      <div className="text-muted-foreground/70 text-[10px]">Credited to closer</div>
                    </div>
                    <span className="font-semibold whitespace-nowrap">$30 / month</span>
                  </div>
                  <div className="border-t" />
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-medium">One-shot</div>
                      <div className="text-muted-foreground">Resolved in a single response</div>
                      <div className="text-muted-foreground/70 text-[10px]">Credited to closer</div>
                    </div>
                    <span className="font-semibold whitespace-nowrap">$2 / ticket</span>
                  </div>
                  <div className="border-t" />
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-medium">Quality + Collab</div>
                      <div className="text-muted-foreground">Peer nomination, biweekly</div>
                      <div className="text-muted-foreground/70 text-[10px]">$30 split if 2 winners</div>
                    </div>
                    <span className="font-semibold whitespace-nowrap">$30 / period</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Select value={selectedIndex} onValueChange={setSelectedIndex}>
            <SelectTrigger className="w-[200px]">
              <span>{selected.label}</span>
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.index} value={opt.index}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-muted-foreground text-center py-16">
            Failed to load incentive metrics
          </p>
        ) : !data || data.agents.length === 0 ? (
          <p className="text-muted-foreground text-center py-16">
            No agent data for this period
          </p>
        ) : (
          <div className="grid grid-cols-12 gap-5">
            {/* Left: Agent cards */}
            <div className="col-span-8 space-y-3">
              {[...data.agents]
                .sort((a, b) => {
                  const aActive = ACTIVE_AGENT_EMAILS.includes(a.agentEmail);
                  const bActive = ACTIVE_AGENT_EMAILS.includes(b.agentEmail);
                  if (aActive && !bActive) return -1;
                  if (!aActive && bActive) return 1;
                  return 0;
                })
                .map((agent, i) => (
                  <AgentCard
                    key={agent.agentEmail}
                    agent={agent}
                    index={i}
                    expanded={expandedAgents.has(agent.agentEmail)}
                    onToggle={() => toggleAgent(agent.agentEmail)}
                    startDate={startDate}
                    endDate={endDate}
                    inactive={!ACTIVE_AGENT_EMAILS.includes(agent.agentEmail)}
                  />
                ))}
            </div>

            {/* Right: Sidebar */}
            <div className="col-span-4">
              <Sidebar
                periodKey={periodKey}
                agents={data.agents}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
